
import PaginatedSearch from "@/mixins/PaginatedSearch";
import AreaEventHistoryColumnsMixin from "@/mixins/AreaEventHistoryColumnsMixin";
import { Mixins, Component, Prop, Watch } from "vue-property-decorator";
import { TimeZone, PaginatedSearchQueryParams, UserPermissions, PaginatedQueryAndSearchParams } from "@/store/types";
import { Getter, Action, namespace } from "vuex-class";
import {
	AreaEventHistoryPaginatedSearchParams,
	AreaEventHistoryTypeIds,
	AreaEventHistory as AreaEventHistoryType,
	AreaEventHistoryTypeNames,
	AreaEventHistoryColumnKey
} from "@/types/sv-data/AreaEventHistory";
import DatePicker from "@sureview/vuejs-datepicker";
import vselect3 from "vselect3";
import GenericTable from "../table/generic-table.vue";
import { ModalItem } from "../table/generic-update-modal.vue";
import api from "@/services/api.service";
import { truncateString } from "@/filters";
import axios, { CancelTokenSource } from "axios";
import { debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import { Response } from "@/store/responses/types";
import ConfirmationModal from "../mobile-raise-templates/ConfirmationModal.vue";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import { FilteredEvent } from "@/store/eventqueue/types";
import { EventShareUser } from "@/store/site-monitor/types";
import { Modules } from "@/store/ModuleTypes";
import { nameToInitials } from "@/utils/string-utils";

const SiteMonitor = namespace(Modules.SiteMonitor);
const AreaEventHistoryStore = namespace(Modules.AreaEventHistory);
const EventQueue = namespace(Modules.EventQueue);

@Component({
	components: {
		"date-picker": DatePicker,
		"v-select3": vselect3,
		"generic-table": GenericTable,
		"confirmation-modal": ConfirmationModal
	},
	filters: {
		truncateString
	}
})
export default class AreaEventHistory extends Mixins(AreaEventHistoryColumnsMixin, PaginatedSearch, RegionalHandlerMixin) {
	@Getter getTimeZone: (timeZoneId: number) => TimeZone;
	@Getter getTimeZoneLabel: (timeZone: TimeZone) => string;
	@Action openEventReview: ({ eventId, hideNav }: { eventId: number, hideNav?: boolean }) => void;
	@SiteMonitor.Mutation setActivity: () => void;
	@SiteMonitor.Mutation setIncidentReportShown: (shown: boolean) => void;
	@SiteMonitor.Action leaveEventRequest: (eventId: number) => Promise<any>;
	@SiteMonitor.Action parkEvent: (payload: { eventId: number; hours: number; minutes: number; seconds: number; parkNote: string; parkedByMap: boolean }) => Promise<any>;
	@SiteMonitor.Action eventPassControl: (payload: { eventID: number; userID: number }) => Promise<any>;
	@SiteMonitor.Getter getEventShare: EventShareUser[];
	@SiteMonitor.Getter("getIsController") isController!: boolean;

	@AreaEventHistoryStore.Mutation setIsPopoutModeEnabled: (isEnabled: boolean) => void;
	@AreaEventHistoryStore.Mutation setFilter: (filter: AreaEventHistoryPaginatedSearchParams) => void;
	@AreaEventHistoryStore.Mutation setTimeZone: (timeZoneId: number) => void;
	@AreaEventHistoryStore.Mutation resetState: () => void;

	@AreaEventHistoryStore.State private readonly areaEventHistory: AreaEventHistoryType[];
	@AreaEventHistoryStore.State private readonly totalRecords: number;
	@AreaEventHistoryStore.State private readonly isPopoutModeEnabled: boolean;
	@AreaEventHistoryStore.State private readonly filter: AreaEventHistoryPaginatedSearchParams;

	@AreaEventHistoryStore.Action fetchAreaEventHistory: () => Promise<void>;

	@EventQueue.Getter getJoiningEventID: any;

	@Getter getPermissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getUserId: number;

	@Prop({ type: Number, required: true }) private readonly groupId: number;
	@Prop({ type: Number, required: false }) private readonly timeZoneId: number;
	@Prop({ type: Boolean, required: false }) private readonly poppedOut: boolean;

    private isDestroyed: boolean = false;
    private readonly fallbackUtcTimezoneId: number = 35;
    private readonly responseCancelSearchMessage: string = "Cancel Search";

	private newDataFilter: AreaEventHistoryPaginatedSearchParams = {
		fromDate: this.createFromDate(),
		toDate: this.createToDate(),
		groupId: null,
		eventHistoryTypeIds: [1],
		includeAutohandled: false,
		responseIds: [],
		page: 1,
		pageSize: 25,
		searchTerm: "",
	};

	private internalFilter: AreaEventHistoryPaginatedSearchParams = {
		...this.newDataFilter,
	};

	private searchedResponses: Response[] = [];
	private paginationParams: PaginatedQueryAndSearchParams;
	private isLoading: boolean = false;
	private isFilterDataValid: boolean = true;
	private validationErrorMessage: string = "";
	private modalItems: ModalItem[] = [];
	private showNavigationModal: boolean = false;
	private pendingNavigationRow: FilteredEvent;


	private responseSearchToken: CancelTokenSource | null = null;

	private get disabledDates(): any {
		let date = new Date();
		date.setDate(date.getDate() - 31);
		return { to: date };
	}

	private get getTimeZoneName(): string {
		return this.getTimeZoneLabel(this.timeZone);
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
			await this.fetchResponses(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true });

    /**
     * @summary Fetches the responses based on the search term for the selected area.
     * @param {string} searchTerm The search term to filter responses.
     * @param {boolean} isNewGroup Indicates if a new group ID is selected. Allows empty search term to fetch the initial set of responses.
     * @returns {Promise<void>}
     */
	private async fetchResponses(searchTerm: string, isNewGroup: boolean = false): Promise<void> {
        if ((!searchTerm && !isNewGroup) || !this.groupId) {
            return;
        }
        
		if (this.responseSearchToken) {
			this.responseSearchToken.cancel(this.responseCancelSearchMessage);
		}

		this.responseSearchToken = axios.CancelToken.source();

		try {
			const responses: Response[] = await api.getAllResponsesByArea(this.groupId, searchTerm);
			
            // Add responses to searchedResponses, ensuring no duplicates
			for (const response of responses) {
				if (!this.searchedResponses.some((existingResponse: Response) => existingResponse.responseID === response.responseID)) {
					this.searchedResponses.push(response);
				}
			}
		} catch (e) {
			return; // Return when past requests are cancelled
		}
	}

	protected get columnDescriptions(): Record<AreaEventHistoryColumnKey, string> {
		return {
			[AreaEventHistoryColumnKey.CreatedAreaTime]: this.getTimeZoneName,
			[AreaEventHistoryColumnKey.ClosedAreaTime]: this.getTimeZoneName,
		} as Record<AreaEventHistoryColumnKey, string>;
	}

	private readonly fallbackTimeZone: TimeZone =
		{
			timeZoneID: 35,
			id: "UTC",
			iana: "Etc/Utc",
			displayName: ""
		};

	private get timeZone(): TimeZone {
		return this.getTimeZone(this.timeZoneId || this.fallbackUtcTimezoneId) || this.fallbackTimeZone;
	}

	private get eventHistoryTypeOptions(): { eventTypeId: number, eventTypeName: string }[] {
		const options = [
			{
				eventTypeId: AreaEventHistoryTypeIds.Event,
				eventTypeName: AreaEventHistoryTypeNames.Event
			},
			{
				eventTypeId: AreaEventHistoryTypeIds.Masking,
				eventTypeName: AreaEventHistoryTypeNames.Masking
			}
		];

		if (this.onTestFeatureEnabled) {
			options.push({
				eventTypeId: AreaEventHistoryTypeIds.OnTest,
				eventTypeName: AreaEventHistoryTypeNames.OnTest
			});
		}

		return options;
	}

	private showLiveData: boolean = false;
	private showAdvancedFilters: boolean = true;
	private liveDataTimeoutId: NodeJS.Timeout = null;

	public get sitRepEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "SitRep"]) && this.getPermissions.canViewSitRep;
	}

	private created(): void {
		this.setDefaultDateRange();
	}

	private setDefaultDateRange(): void {

		// set the fromDate based on the Pref, but fall back to 1 day if its not set.
		const defaultDate = this.tenantSystemPrefs.eventHistoryDefaultSearchDays ? this.createFromDate(this.tenantSystemPrefs.eventHistoryDefaultSearchDays) : this.createFromDate();

		this.newDataFilter.fromDate = defaultDate;
		this.newDataFilter.toDate = this.createToDate();
	}

	private async mounted(): Promise<void> {
        try {
			const useDefaultAutohandled = this.configureDefaultAutohandledEnabled && this.tenantSystemPrefs?.areaEventHistorySettings?.defaultAutohandled != null;

			if (!this.filter) {

				this.internalFilter = {
					...this.newDataFilter
				};

				if (useDefaultAutohandled) {
					this.internalFilter.includeAutohandled = this.tenantSystemPrefs?.areaEventHistorySettings?.defaultAutohandled;
				}

				await this.getAreaEventHistory();
			}
			else {
				this.newDataFilter = {
					...this.filter,
				}

				if (useDefaultAutohandled) {
					this.newDataFilter.includeAutohandled = this.tenantSystemPrefs?.areaEventHistorySettings?.defaultAutohandled;
				}
			}
        } catch (err) {
            console.error("Failed to load Area Event History data.", err);
        }
    }

	private beforeDestroy(): void {
		this.isDestroyed = true;
		clearTimeout(this.liveDataTimeoutId);

		if (!this.isPopoutModeEnabled) {
			this.resetState();
		}
	}

	private createToDate(): Date {
		var toDate = new Date(Date.now());
		// Set date to 23:59:59.999 for today's date
		toDate.setHours(23, 59, 59, 999);
		return toDate;
	}

	private createFromDate(days: number = 1): Date {
		let currentMilliseconds = Date.now();
		// Set date to 12:00AM, 1 day before current date/time
		let fromDate = new Date(currentMilliseconds - days * 24 * 60 * 60 * 1000);
		fromDate.setHours(0, 0, 0, 0);
		return fromDate;
	}

	private canRenderEventLink(event: AreaEventHistoryType): boolean {
		// When the event type is Masking AND the feature isnt enabled, return false. For all other cases, return true
		return !(event.type === AreaEventHistoryTypeNames.Masking && !this.maskingFeatureEnabled);
	}

	private incidentReport(): void {
		this.setIncidentReportShown(true);
		this.setActivity();
	}

	private autoPassAlarmEnabled(): boolean {
		return this.getFeature(["Alarms.AutoPassAlarmOnLeave"]);
	}

	private get onTestFeatureEnabled(): boolean {
		return this.getFeature(["Areas","AreaEventHistory","OnTest"])
	}

	private get maskingFeatureEnabled(): boolean {
		return this.getFeature(["Areas","AreaEventHistory","OpenMaskedEvents"])
	}

	private get searchFeatureEnabled(): boolean {
		return this.getFeature(["Areas","AreaEventHistory","Search"])
	}

	private get configureDefaultAutohandledEnabled(): boolean {
		return this.getFeature(["Areas", "AreaEventHistory", "ColumnConfiguration", "DefaultAutohandled"]);
	}

	private async leaveEvent(): Promise<any> {
		if (this.getJoiningEventID) {
			if (this.isController) {
				// Handle auto-pass alarm if enabled
				if (this.autoPassAlarmEnabled()) {
					let sharedOperators = this.getEventShare.filter((user: any) =>
						user.userID != null &&
						user.userID != this.getUserId &&
						!user.removed &&
						user.isActive
					);
					if (sharedOperators != null && sharedOperators.length == 1) {
						await this.eventPassControl({
							eventID: this.getJoiningEventID,
							userID: sharedOperators[0].userID
						});
					} else {
						// Park the event if we can't pass control
						await this.parkEvent({
							eventId: this.getJoiningEventID,
							hours: 0,
							minutes: 0,
							seconds: 0,
							parkNote: "",
							parkedByMap: false
						});
					}
				} else {
					// Park the event if auto-pass is not enabled
					await this.parkEvent({
						eventId: this.getJoiningEventID,
						hours: 0,
						minutes: 0,
						seconds: 0,
						parkNote: "",
						parkedByMap: false
					});
				}
			} else {
				// If not controller, just leave the event
				await this.leaveEventRequest(this.getJoiningEventID);
			}
		}
	}

	private convertToFilteredEvent(areaEvent: AreaEventHistoryType): FilteredEvent {
		return {
		age: 0,
		alarmCount: 0,
		alarmTypeID: null,
		awaitingRestoreCount: 0,
		created: areaEvent.created as unknown as string,
		displayTitle: areaEvent.details || '',
		eventID: areaEvent.eventId,
		eventTitle: areaEvent.details || '',
		eventTypeID: 1,
		externalSystemInfo: null,
		groupCreated: areaEvent.groupCreated as unknown as string,
		groupID: this.groupId,
		groupTitle: '',
		groupUnparkAt: null,
		inFilters: null,
		inProcessing: false,
		isCCure: false,
		isTrouble: false,
		lastActivity: null,
		lastUnparkedAt: '',
		location: '',
		needsSilence: false,
		priority: areaEvent.priority,
		raisedByVideowall: false,
		regionID: 0,
		regionTitle: '',
		regionURL: '',
		responseID: null,
		responseTitle: '',
		routingGroupColourCode: '',
		routingGroupID: 0,
		routingGroupTitle: '',
		timezone: null,
		unParkAt: null,
		userID: null,
		userUnparkAt: null,
		username: areaEvent.user || '',
		viewedByVideowall: false,
		priorityStyle: '',
		slaBreachAt: null,
		slaBreachRaised: null
		};
  	}

	private handleEventClick(row: AreaEventHistoryType): void {
		if (!this.canRenderEventLink(row)) {
			return;
		}

		if (!row.closed) {
			this.pendingNavigationRow = this.convertToFilteredEvent(row);
			this.showNavigationModal = true
		} else {
			this.openEventReview({eventId: row.eventId, hideNav: true});
		}
	}

	private async handleNavigationConfirm(): Promise<void> {
		await this.leaveEvent();
        await this.handleEventQueueItem(
            this.pendingNavigationRow,
            undefined,
            undefined,
            'Picked up from Area Event History',
			false,
			true
        );
		this.showNavigationModal = false;
		this.pendingNavigationRow = null;
	}

	private handleNavigationCancel(): void {
		this.showNavigationModal = false;
		this.pendingNavigationRow = null;
	}

    /**
     * @summary Handler for when the groupId prop changes.
     * @returns {Promise<void>}
     */
	@Watch("groupId", { immediate: true, })
	private async updateFilterGroupId(): Promise<void> {
        if (this.groupId) {
            this.newDataFilter.groupId = this.groupId;
            this.setFilter(this.newDataFilter);
            await this.getAreaEventHistory();
            await this.fetchResponses("", true);
        }
	}

    private async fetchHistoryWithNewData(): Promise<void> {
		this.internalFilter = { ...this.newDataFilter };
        await this.getAreaEventHistory();
    }

	private async getAreaEventHistory(): Promise<void> {
		if (!this.showLiveData) {
			this.isLoading = true;
		}

        try {
            if (this.internalFilter.groupId) {
				if (this.searchFeatureEnabled && this.paginationParams != null) {
					// If search is enabled and a search term is provided, include it in the pagination parameters
					// Just to ensure that the generic-table default search value is not used.
					this.paginationParams.searchTerm = this.newDataFilter.searchTerm;
				}

                var request: AreaEventHistoryPaginatedSearchParams = {
                    ...this.internalFilter,
                    ...this.paginationParams
                }
				
                this.setFilter(request);
                await this.fetchAreaEventHistory();
            }
        } catch (err) {
            console.error("Failed to retrieve Area Event History.", err);
        } finally {
            this.isLoading = false;
        }
    }

	private get mapAreaEventHistoryData(): AreaEventHistoryType[] {
		const currentEventId = this.getJoiningEventID;
		let mappedEvents: AreaEventHistoryType[] = this.areaEventHistory
		.filter(history => history.eventId !== currentEventId)
		.map(history => {
			if (history.closed && !history.category) {
                history.category = "No Category Assigned";
            }

			return ({
				...history,
				userInitials: nameToInitials(history.user),
				createdAreaTime:  history.groupCreated ? new Date(history.groupCreated).toLocaleString(undefined).toUpperCase() : "",
				createdLocalTime: history.created ? new Date(history.created).toLocaleString(undefined).toUpperCase() : "",
				closedAreaTime: history.groupClosed? new Date(history.groupClosed).toLocaleString(undefined).toUpperCase() : "",
				closedLocalTime: history.closed ? new Date(history.closed).toLocaleString(undefined).toUpperCase() : "",
				actions: null
			});
		});

		return mappedEvents;
	}

	private get liveDataStatus(): string {
		return this.showLiveData ? "Live Data On" : "Live Data Off";
	}

	private get liveDataDivClass(): string {
		return this.poppedOut ? "live-data-popped-out" : 
			this.searchFeatureEnabled ? "" : "live-data" // If search feature is enabled, don't apply the live-data class to avoid layout issues;
	}

	private get liveDataButtonClass(): string {
		return this.showLiveData ? "fas fa-toggle-on" : "fas fa-toggle-off";
	}

	private toggleLiveData(): void {
		this.showLiveData = !this.showLiveData;
		if (this.showLiveData) {
			this.pollLiveData();
		}
	}

	private toggleAdvancedFilters(): void {
		this.showAdvancedFilters = !this.showAdvancedFilters;
	}

	private enablePopoutMode(): void {
		this.$bvModal.hide("leaveModal");
		this.setTimeZone(this.timeZoneId);
		this.setIsPopoutModeEnabled(true);
	}

	@Watch("isDestroyed")
	private async pollLiveData(): Promise<void> {
		if (!this.showLiveData || this.isDestroyed) {
			clearTimeout(this.liveDataTimeoutId);
		} else {
			await this.getAreaEventHistory();
			this.liveDataTimeoutId = setTimeout(this.pollLiveData, 10000);
		}
	}

	@Watch("newDataFilter.fromDate")
	@Watch("newDataFilter.toDate")
	@Watch("newDataFilter.eventHistoryTypeIds")
	private validateFilterDates(): void {
		if (!this.newDataFilter || !this.newDataFilter.fromDate || !this.newDataFilter.toDate) {
			return;
		}

		this.sanitiseDatePickerValues();

		if (this.newDataFilter.eventHistoryTypeIds.length < 1) {
			this.newDataFilter.eventHistoryTypeIds.push(1);
			return;
		}

		if (this.newDataFilter.fromDate > this.newDataFilter.toDate) {
			this.isFilterDataValid = false;
			this.validationErrorMessage = "'To' date cannot be before the 'From' date";
			return;
		}
		let absoluteDifference = this.newDataFilter.toDate.getTime() - this.newDataFilter.fromDate.getTime();
		let differenceInDays = absoluteDifference / (1000 * 60 * 60 * 24);
		if (differenceInDays >= 32) {
			this.isFilterDataValid = false;
			this.validationErrorMessage = "Date range cannot exceed 31 days";
			return;
		}

		this.isFilterDataValid = true;
		this.validationErrorMessage = "";
	}

	private sanitiseDatePickerValues(): void {
		try {
			if (typeof(this.newDataFilter.fromDate) == "string")
			{
				this.newDataFilter.fromDate = new Date(this.newDataFilter.fromDate);
				if (isNaN(this.newDataFilter.fromDate.getTime())) {
					this.newDataFilter.fromDate = this.createFromDate();
				}
			}
		} catch {
			this.newDataFilter.fromDate = this.createFromDate();
		}

		try {
			if (typeof(this.newDataFilter.toDate) == "string")
			{
				this.newDataFilter.toDate = new Date(this.newDataFilter.toDate);
				if (isNaN(this.newDataFilter.toDate.getTime())) {
					this.newDataFilter.toDate = this.createToDate();
				}
			}
		} catch {
			this.newDataFilter.toDate = this.createToDate();
		}
	}

	private async updateData(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		this.paginationParams = paginatedSearchQueryParams;
		await this.getAreaEventHistory();
	}

	private get selectedEventHistoryTypes(): number[] {
		return this.newDataFilter.eventHistoryTypeIds;
	}

	private set selectedEventHistoryTypes(types: number[]) {
		this.newDataFilter.eventHistoryTypeIds = types;
	}

	private get parentDivClass(): string {
		return this.searchFeatureEnabled ? "history-filter-container history-filter-container_col" : "";
	}

	private get subDivClass(): string {
		return this.searchFeatureEnabled ? "primary-filters-row" : "history-filter-container history-filter-container_wrap";
	}
}
